import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import './index.css';
import App from './App';
import { Provider } from 'react-redux';
import { EventType, PublicClientApplication } from '@azure/msal-browser';
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin, ErrorBoundary } from '@datadog/browser-rum-react';
import configureStore from './redux';
import CustomError from './Components/Global/ErrorBoundary';
import { SnackbarProvider } from 'notistack';
import { dataDogApplicationId, dataDogClientToken, dataDogEnv } from './constants';
import { msalConfig } from './constants/msal';
import ActionSnack from './Components/Global/Snacks/ActionSnack';

const store = configureStore();

if (process.env.REACT_APP_VERSION === 'production' || process.env.REACT_APP_DD_ENABLED === 'true') {
  datadogRum.init({
    applicationId: dataDogApplicationId,
    clientToken: dataDogClientToken,
    site: 'datadoghq.eu',
    service: 'qhub-frontend',
    env: dataDogEnv,
    version: process.env.REACT_APP_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0,
    defaultPrivacyLevel: 'mask-user-input',
    plugins: [reactPlugin({ router: false })],
  });

  datadogRum.setUser({
    id: localStorage.getItem('_id') || '',
    name: localStorage.getItem('name') || '',
  });
}

const msalInstance = new PublicClientApplication(msalConfig);

// Default to using the first account if no account is active on page load
if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
  // Account selection logic is app dependent. Adjust as needed for different use cases.
  msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
}

msalInstance.addEventCallback((event) => {
  if (
    (event.eventType === EventType.LOGIN_SUCCESS ||
      event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
      event.eventType === EventType.SSO_SILENT_SUCCESS) &&
    event.payload.account
  ) {
    msalInstance.setActiveAccount(event.payload.account);
  }
});

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <>
    <ErrorBoundary fallback={CustomError}>
      <Provider store={store}>
        <Router
          getUserConfirmation={() => {
            /* Empty callback to block the default browser prompt */
          }}
        >
          <SnackbarProvider maxSnack={10} Components={{ actionSnack: ActionSnack }}>
            <App instance={msalInstance} />
          </SnackbarProvider>
        </Router>
      </Provider>
    </ErrorBoundary>
  </>,
);
